import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { StyledStickyContainer } from "@src/components";
import { Footer, Header } from "@src/features";
import { zIndex } from "@src/styles";

import { BaseLayout, BaseLayoutProps } from "../BaseLayout";
import {
  LdBreadcrumbs,
  LdCrumb,
  LdButton,
  LdNotice,
  LdTypo,
} from "@emdgroup-liquid/liquid/dist/react";
import {
  StyledContainer,
  StyledCrumbHome,
  StyledCrumbText,
} from "@src/components/StyledStickyContainer/StyledStickyContainer";
import { useEffect, useState } from "react";
import { paths } from "@src/router/paths";
import {
  isShowNotification,
  firstNotification,
  secondNotification,
} from "../../config";

type MainLayoutProps = Omit<BaseLayoutProps, "children">;

function MainLayout({
  primary,
  secondary,
  showHeader,
  showFooter,
}: MainLayoutProps) {
  const [inputPage, setInputPage] = useState<boolean>(true);
  const [outputPage, setOutputPage] = useState<boolean>(false);
  const [offtargetPage, setOfftargetPage] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === paths.offtarget) {
      setOfftargetPage(true);
      setOutputPage(true);
    } else if (location.pathname === paths.output) {
      setOfftargetPage(false);
      setInputPage(true);
      setOutputPage(true);
    } else {
      setOfftargetPage(false);
      setInputPage(true);
      setOutputPage(false);
    }
  }, [location.pathname]);

  const onNavigate = (path: string) => {
    navigate(path);
  };

  const renderNavigation = () => (
    <LdBreadcrumbs>
      <StyledCrumbHome href="#" key="dna">
        <StyledCrumbText>
          <LdButton brandColor mode="ghost">
            DNA Base Editor
          </LdButton>
        </StyledCrumbText>
      </StyledCrumbHome>
      {inputPage && (
        <LdCrumb href="" key="seq-input">
          <StyledCrumbText>
            <LdButton
              brandColor
              mode="ghost"
              onClick={() => onNavigate(`${paths.root}/`)}
            >
              Sequence Input
            </LdButton>
          </StyledCrumbText>
        </LdCrumb>
      )}
      {outputPage && (
        <LdCrumb href="#" key="output-details">
          <StyledCrumbText>
            <LdButton brandColor mode="ghost" onClick={() => navigate(-1)}>
              Output Details
            </LdButton>
          </StyledCrumbText>
        </LdCrumb>
      )}
      {offtargetPage && (
        <LdCrumb href="#" key="pot-offttarget">
          <StyledCrumbText>
            <LdButton
              brandColor
              mode="ghost"
              onClick={() => onNavigate(`${paths.offtarget}/`)}
            >
              Potential Off-targets
            </LdButton>
          </StyledCrumbText>
        </LdCrumb>
      )}
    </LdBreadcrumbs>
  );

  return (
    <BaseLayout primary={primary} secondary={secondary}>
      {showHeader && (
        <StyledStickyContainer zIndex={zIndex.header}>
          <Header />
        </StyledStickyContainer>
      )}
      <StyledContainer zIndex={zIndex.header}>
        {renderNavigation()}
      </StyledContainer>
      {isShowNotification && (
        <LdNotice headline="Information" mode="warning">
          <LdTypo>
            {firstNotification.first_part}
            <Link
              to={firstNotification.link_url}
              target="_blank"
              style={{ textDecoration: "none" }}
            >
              {firstNotification.second_part}
            </Link>
          </LdTypo>
          <LdTypo>
            {secondNotification.first_part}
            <Link
              to={secondNotification.link_url}
              target="_blank"
              style={{ textDecoration: "none" }}
            >
              {secondNotification.second_part}
            </Link>
          </LdTypo>
        </LdNotice>
      )}
      <main>
        <Outlet />
      </main>
      {showFooter && <Footer />}
    </BaseLayout>
  );
}

export { MainLayout };
